import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { Country } from "@omni/types/enums/country.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useCallback, useState } from "react";

import BPostService, {
    AddressValidationParams
} from "@/services/bpost.service";

type OnValidCallback = (addressLine: string) => void;

type AddressFormBlockContextValue = {
    isValidating: boolean;
    addPrefix: (fieldName: string, separator?: string) => string;
    addressStatus: AddressValidationStatus;
    disabled?: boolean;
    displayAddressFields: boolean;
    setDisplayAddressFields: (display: boolean) => void;
    validate: (
        values: AddressValidationParams,
        cbOnValid: OnValidCallback
    ) => Promise<void>;
};

export type AddressFormBlockContextProviderProps = {
    addressStatus: AddressValidationStatus;
    addressType: AddressType;
    disabled?: boolean;
    skipValidation?: boolean;
    country?: string;
};

export const AddressFormBlockContext = createContext<
    AddressFormBlockContextValue | undefined
>(undefined);

AddressFormBlockContext.displayName = "AddressFormBlockContext";

export const AddressFormBlockContextProvider = ({
    addressStatus,
    addressType,
    disabled,
    skipValidation,
    country = Country.Be,
    children
}: RequireChildrenFor<AddressFormBlockContextProviderProps>) => {
    const [displayAddressFields, setDisplayAddressFields] = useState(
        addressStatus === AddressValidationStatus.NoValidationNeeded ||
            country !== Country.Be
    );
    const [isValidating, setIsValidating] = useState(false);

    const addPrefix = useCallback(
        (fieldName: string) => {
            const prefix =
                addressType === AddressType.Default ? "" : addressType;
            return `${prefix.toLowerCase()}-${fieldName}`;
        },
        [addressType]
    );

    const validate = useCallback(
        async (values: AddressValidationParams, cbOnValid: OnValidCallback) => {
            if (
                skipValidation ||
                addressStatus === AddressValidationStatus.NoValidationNeeded
            ) {
                return;
            }

            setIsValidating(true);

            const validationResult = await BPostService.validateAddress(values);

            setDisplayAddressFields(!validationResult.isValid);

            if (validationResult.isValid && validationResult.officialAddress) {
                cbOnValid(validationResult.officialAddress?.line);
            }
            setIsValidating(false);
        },
        [addressStatus, skipValidation]
    );

    return (
        <AddressFormBlockContext.Provider
            value={{
                isValidating,
                addPrefix,
                addressStatus,
                disabled,
                displayAddressFields,
                setDisplayAddressFields,
                validate
            }}
        >
            {children}
        </AddressFormBlockContext.Provider>
    );
};

export const useAddressFormBlockContext = (): AddressFormBlockContextValue => {
    const context = use<AddressFormBlockContextValue | undefined>(
        AddressFormBlockContext
    );
    if (!isDefined(context)) {
        throw new Error(`${AddressFormBlockContext.displayName} is undefined.`);
    }
    return context;
};
